/**
 * OhCharteGallery — galerie de recette des composants normés charte.
 *
 * But : prouver visuellement, en mode CLAIR et FONCÉ, que les composants
 * normés (oh-charte.jsx) dérivent uniquement des tokens `var(--oh-*)` et
 * basculent correctement entre les deux modes (charte §1/§6).
 *
 * Route : /charte-gallery (artefact de recette, sans effet de bord, lecture seule).
 * Le bouton en haut à droite bascule le thème (écrit `omnihub-theme` + classe
 * .light/.dark sur documentElement, comme ThemeContext) — utile en manuel ; les
 * captures automatiques forcent le mode via localStorage avant boot.
 */

import React, { useState, useEffect } from "react";
import {
  OhCard, OhCardHeader, OhStatusNote,
  OhInput, OhSelect, OhField,
  OhSideMenu, OhMenuSection, OhMenuItem,
} from "../../components/ui/oh-charte";
import { Home, FileText, Settings, Users } from "lucide-react";

function applyTheme(mode) {
  const r = document.documentElement;
  r.classList.remove("light", "dark");
  r.classList.add(mode);
  try { localStorage.setItem("omnihub-theme", mode); } catch {}
}

export default function OhCharteGallery() {
  const [mode, setMode] = useState(
    () => (typeof document !== "undefined" && document.documentElement.classList.contains("light") ? "light" : "dark")
  );
  const [active, setActive] = useState("dossiers");
  useEffect(() => { applyTheme(mode); }, [mode]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--oh-bg)", color: "var(--oh-text)", padding: "var(--oh-space-6)" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* En-tête galerie */}
        <div className="flex items-center justify-between" style={{ marginBottom: "var(--oh-space-6)" }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.3 }}>Composants normés — charte OmniHub</h1>
            <p style={{ fontSize: 14, color: "var(--oh-text-secondary)", lineHeight: 1.6 }}>
              Carte · encart d'état · input/select · menu latéral — 100 % tokens dual-mode.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMode((m) => (m === "dark" ? "light" : "dark"))}
            data-testid="gallery-theme-toggle"
            style={{
              height: 36, padding: "0 var(--oh-space-3)", borderRadius: "var(--oh-radius-control)",
              border: "1px solid var(--oh-accent)", background: "transparent", color: "var(--oh-accent-text)",
              fontSize: 14, fontWeight: 500, cursor: "pointer",
            }}
          >
            Mode : {mode === "dark" ? "foncé" : "clair"}
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: "var(--oh-space-6)", alignItems: "start" }}>
          {/* Menu latéral normé */}
          <OhCard padded data-testid="gallery-menu">
            <OhSideMenu>
              <OhMenuSection label="Navigation" />
              <OhMenuItem icon={<Home className="w-4 h-4" />} active={active === "accueil"} onClick={() => setActive("accueil")}>Accueil</OhMenuItem>
              <OhMenuItem icon={<FileText className="w-4 h-4" />} active={active === "dossiers"} onClick={() => setActive("dossiers")}>Dossiers</OhMenuItem>
              <OhMenuItem icon={<Users className="w-4 h-4" />} active={active === "clients"} onClick={() => setActive("clients")}>Clients</OhMenuItem>
              <OhMenuSection label="Paramètres" />
              <OhMenuItem icon={<Settings className="w-4 h-4" />} active={active === "config"} onClick={() => setActive("config")}>Configuration</OhMenuItem>
            </OhSideMenu>
          </OhCard>

          {/* Colonne droite */}
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--oh-space-6)" }}>
            {/* Cartes */}
            <OhCard data-testid="gallery-card">
              <OhCardHeader
                title="Carte normée"
                description="Fond élevé, bord 0.5px, radius 12px, padding 16px."
              />
              <p style={{ fontSize: 14, color: "var(--oh-text-secondary)", lineHeight: 1.6 }}>
                Toute surface de contenu dérive de cette carte. Aucune couleur en dur : le fond,
                le texte et le bord basculent automatiquement entre les deux modes.
              </p>
            </OhCard>

            {/* Encarts d'état */}
            <OhCard data-testid="gallery-status">
              <OhCardHeader title="Encarts d'état / messages" description="Paires sémantiques succès / erreur / info, contraste garanti 2 modes." />
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--oh-space-3)" }}>
                <OhStatusNote variant="success" title="DAL généré">Le document a été généré et rattaché au dossier.</OhStatusNote>
                <OhStatusNote variant="error" title="Échec de l'envoi">Le serveur a renvoyé une erreur. Réessayez.</OhStatusNote>
                <OhStatusNote variant="info" title="Synchronisation en cours">Les dossiers ANAH se mettent à jour.</OhStatusNote>
                <OhStatusNote variant="neutral">Aucune action requise pour l'instant.</OhStatusNote>
              </div>
            </OhCard>

            {/* Contrôles de saisie */}
            <OhCard data-testid="gallery-inputs">
              <OhCardHeader title="Input / select" description="Hauteur 36px, radius 8px, focus = anneau accent." />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--oh-space-4)" }}>
                <OhField label="Nom du bénéficiaire" htmlFor="g-nom" hint="Tel qu'indiqué sur la pièce d'identité.">
                  <OhInput id="g-nom" placeholder="Dupont" defaultValue="Dupont" />
                </OhField>
                <OhField label="Type de logement" htmlFor="g-type">
                  <OhSelect id="g-type" defaultValue="maison">
                    <option value="maison">Maison</option>
                    <option value="appartement">Appartement</option>
                  </OhSelect>
                </OhField>
                <OhField label="Téléphone" htmlFor="g-tel" error="Numéro invalide.">
                  <OhInput id="g-tel" invalid placeholder="06 12 34 56 78" defaultValue="06 12" />
                </OhField>
                <OhField label="Surface habitable" htmlFor="g-surf" required>
                  <OhInput id="g-surf" placeholder="90 m²" />
                </OhField>
              </div>
            </OhCard>
          </div>
        </div>
      </div>
    </div>
  );
}
